import React, { useEffect, useState, useCallback } from 'react';
import { ArrowLeft, MapPin, Calendar, Heart, MessageCircle, CalendarCheck, DollarSign, X, ChevronRight, ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom'; 

import { ApiService } from '../../utils/ApiService';
import { checkImageLoad, formatDateTime, isValidUrl } from '../../common/Functions';
import { capitalizeEachWord } from '../../utils/common';
import FullPageLoader from '../../common/FullPageLoader';

const EventDetailPage = ({ eventId, selectedEvent, setSelectedEvent }) => {
  const navigate = useNavigate();
  const [showGallery, setShowGallery] = useState(null); 
  const [galleryError, setGalleryError] = useState('');
  const [eventDetails, setEventDetails] = useState(null);
  const [firstImage, setFirstImage] = useState(null);
  const [imageValidityMap, setImageValidityMap] = useState({}); 
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const images = eventDetails?.media || [];
  const [loading, setLoading] = useState(true);

  const handleGetEventDetails = useCallback(async () => {
    if (!eventId) return
    try {
      const res = await ApiService.get(`/admin/event?eventId=${eventId}`);
      setEventDetails(res.event);
      setTimeout(() => {
        setLoading(false)
      }, 400)
    } catch (error) {
      console.log("Error fetching Event Details:", error);
    }
  }, [eventId]);

  const getFirstValidImage = useCallback(async (images) => {
    if (!images?.length) return null;

    for (let img of images) {
      if (isValidUrl(img)) {
        const isGood = await checkImageLoad(img);
        if (isGood) return img;
      }
    }
    return null;
  }, []);

  // --- New Logic for Image Validity Map and Gallery Click ---

  useEffect(() => {
    const fetchValidImagesAndMap = async () => {
      const images = eventDetails?.media;
      if (!images?.length) return;

      const img = await getFirstValidImage(images);
      setFirstImage(img);

      // Pre-check all image validities for the gallery section
      const validityMap = {};
      for (let imgUrl of images) {
        if (isValidUrl(imgUrl)) {
          const isGood = await checkImageLoad(imgUrl);
          validityMap[imgUrl] = isGood;
        } else {
          validityMap[imgUrl] = false; // Treat non-URL as invalid
        }
      }
      setImageValidityMap(validityMap);
    };

    if (eventDetails) fetchValidImagesAndMap();
  }, [eventDetails, getFirstValidImage]);

  useEffect(() => {
    handleGetEventDetails();
  }, [handleGetEventDetails]);

  const handleGalleryClick = () => {
    setActiveIndex(0);      // open at first image
    setIsGalleryOpen(true);
  };
  

  const closeModal = () => {
    setShowGallery(null);
    setGalleryError('');
  }

  console.log("IMAGES --> ", images)

  return (
    <>
      {loading && <FullPageLoader />}
      <div className=" bg-gray-100">

        {/* Header */}
        <div className="sticky top-0 bg-gray-100 z-10 pb-4">
          <div className="max-w-8xl mx-auto flex items-center">
            <ArrowLeft
              onClick={() => setSelectedEvent({})}
              className="w-6 h-6 mr-3 cursor-pointer text-gray-700"
            />
            <span className="text-[24px] text-gray-800 font-semibold capitalize">
              {eventDetails?.name || "Event"}
            </span>
          </div>
        </div>

        <div className="pb-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* LEFT COLUMN */}
            <div className="lg:col-span-2 ">

              
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                {/* Hero Image */}
                <div className="relative">
                  <div className="relative">
                    {firstImage ? (
                      <img
                        src={firstImage}
                        alt="Event Hero"
                        className="w-full h-80 object-cover"
                      />
                    ) : (
                      <div className="flex items-center justify-center w-full h-80 bg-gray-200">
                        <h1 className="text-xl font-medium text-gray-500">
                          No Image Found
                        </h1>
                      </div>
                    )}

                    {firstImage && (
                      <button
                        onClick={handleGalleryClick}
                        className="absolute font-medium bottom-4 right-4 text-[#654509] bg-[#FEECCB] px-4 py-2 rounded-full shadow-lg flex items-center gap-2 border border-[#654509]"
                      >
                        Gallery
                      </button>
                    )}
                  </div>

                {/* GALLERY MODAL GOES HERE */}
                {isGalleryOpen && (
                  <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center">

                    {/* CLOSE — RELATIVE TO SCREEN */}
                    <button
                      onClick={() => setIsGalleryOpen(false)}
                      className="fixed top-8 right-10
                      w-10 h-10 rounded-full
                      bg-black/70 text-white
                      flex items-center justify-center
                      hover:bg-black transition z-50"
                    >
                      <X size={20} />
                    </button>

                    {/* IMAGE CONTAINER */}
                    <div className="relative w-[900px] h-[550px] max-w-[90vw] max-h-[80vh] flex items-center justify-center">

                      {/* Left Arrow */}
                      <button
                        onClick={() =>
                          setActiveIndex((prev) =>
                            prev === 0 ? images.length - 1 : prev - 1
                          )
                        }
                        className="absolute left-3 top-1/2 -translate-y-1/2
                        w-12 h-12 rounded-full
                        bg-black/70 text-white
                        flex items-center justify-center
                        hover:bg-black transition"
                      >
                        <ChevronLeft size={28} />
                      </button>

                      {/* Image */}
                      <img
                        src={images[activeIndex]}
                        alt="Gallery"
                        className="max-w-full max-h-full object-contain rounded-lg"
                      />

                      {/* Right Arrow */}
                      <button
                        onClick={() =>
                          setActiveIndex((prev) =>
                            prev === images.length - 1 ? 0 : prev + 1
                          )
                        }
                        className="absolute right-3 top-1/2 -translate-y-1/2
                        w-12 h-12 rounded-full
                        bg-black/70 text-white
                        flex items-center justify-center
                        hover:bg-black transition"
                      >
                        <ChevronRight size={28} />
                      </button>

                    </div>
                  </div>
                )}
                </div>

                <div className='p-6'>
                <div className="border-b pb-6">
                  <div className="flex justify-between items-start mb-3">
                    <div className="text-[20px] font-medium">{eventDetails?.name}</div>
                    <span className="text-l font-medium text-[#654509] bg-[#FEECCB] text-amber-800 px-5 py-2 rounded-md text-sm font-medium ">
                      ${Number(eventDetails?.ticketPrice).toFixed(2)}
                    </span>
                  </div>

                  <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-7 h-7 text-[#797979] self-start" />
                    <span>{eventDetails?.venue?.name || ''}</span>
                  </div>

                    <div className="flex justify-between">
                      <div className="flex items-center gap-3">
                        <Calendar className="w-5 h-5 text-[#797979] self-start" />
                        <span>{formatDateTime(eventDetails?.startTime) || ''}</span>
                      </div>

                      <a href="#" className="text-[#654509] text-m underline hover:text-[#654509] text-normal">
                        Learn more about the event
                      </a>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="bg-white shadow-sm border-b py-6">
                  <h3 className="text-xl font-medium mb-3">Description</h3>
                  <p className="text-gray-600 leading-relaxed">
                    {eventDetails?.description || "No description available."}
                  </p>
                </div>
                {/* Gallery Section */}
                <div className="bg-white rounded-lg shadow-sm pt-6">
                  <h3 className="text-xl font-medium mb-3">Gallery</h3>

                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {eventDetails?.media?.length > 0 ? (
                      eventDetails.media.map((img, idx) => (
                        <img
                          key={idx}
                          src={img}
                          alt={`Gallery ${idx}`}
                          className={`w-full h-32 object-cover rounded-lg cursor-pointer ${imageValidityMap[img] ? 'hover:opacity-90' : 'opacity-50 grayscale'}`}
                          // Use the new click handler
                          onClick={() => handleGalleryClick(img)} 
                        />
                      ))
                    ) : (
                      <div className="text-center text-gray-600 py-6 col-span-5">
                        No data found
                      </div>
                    )}
                  </div>
                </div>
                </div>
              </div>

            </div>

            {/* RIGHT COLUMN */}
            <div className="space-y-6">

              {/* Event Status */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="font-medium text-xl text-black">Event Status</h3>
                  <span className="text-[#654509] bg-[#FEECCB] px-4 py-2 rounded-md text-sm font-medium">
                    {capitalizeEachWord(eventDetails?.status) || 'Unknown'}
                  </span>
                </div>

                <div className="space-y-4 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#4C4C4C]">Start Date & Time</span>
                    <span className="font-normal text-black">
                      {formatDateTime(eventDetails?.startTime) || '-'}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-[#4C4C4C]">End Date & Time</span>
                    <span className="font-normal text-black">
                      {eventDetails?.endTime ? formatDateTime(eventDetails.endTime) : "-"}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-[#4C4C4C]">Capacity</span>
                    <span className="font-normal text-black">{eventDetails?.capacity === -1 ? "Unlimited" : eventDetails?.capacity}</span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-[#4C4C4C]">Book Collection</span>
                    <span className="font-normal text-black">App</span>
                  </div>
                </div>
              </div>

              <hr className="my-6"/>

              {/* Quick Stats */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-xl font-medium mb-6">Quick Stats</h3>

                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-2 xl:grid-cols-4 gap-4 justify-around">

                  <div className="text-center">
                    <div className="w-14 h-14 mx-auto mb-2 rounded-full border-4 border-yellow-400 flex items-center justify-center">
                      <Heart className="w-6 h-6 text-yellow-500" />
                    </div>
                    <div className="text-xs font-bold">Interested</div>
                    <div className="text-xs text-gray-600">{eventDetails?.interestedCount || 0}</div>
                  </div>

                  <div className="text-center">
                    <div className="w-14 h-14 mx-auto mb-2 rounded-full border-4 border-blue-400 flex items-center justify-center">
                      <MessageCircle className="w-6 h-6 text-blue-500" />
                    </div>
                    <div className="text-xs font-bold">Engagement</div>
                    <div className="text-xs text-gray-600">{eventDetails?.engagementCount || 0}</div>
                  </div>

                  <div className="text-center">
                    <div className="w-14 h-14 mx-auto mb-2 rounded-full border-4 border-green-400 flex items-center justify-center">
                      <CalendarCheck className="w-6 h-6 text-green-500" />
                    </div>
                    <div className="text-xs font-bold">Booking</div>
                    <div className="text-xs text-gray-600">{eventDetails?.bookingCount || 0}</div>
                  </div>

                  <div className="text-center">
                    <div className="w-14 h-14 mx-auto mb-2 rounded-full border-4 border-purple-400 flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-purple-500" />
                    </div>
                    <div className="text-xs font-bold">Revenue</div>
                    <div className="text-xs text-gray-600">${eventDetails?.revenue || 0}</div>
                  </div>

                </div>
              </div>

              <hr className="my-6"/>

              {/* Attendees */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="text-xl font-medium mb-4">Attendees</div>

                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {eventDetails?.attendeeList?.length > 0 ? (
                    eventDetails.attendeeList.map((attendee, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg">
                        <img
                          // Fallback for avatar image, though this is a simplification
                          src={attendee?.avatar || "https://i.pravatar.cc/100?u=" + (attendee?.userId?.name || idx)} 
                          className="w-10 h-10 rounded-full object-cover"
                          alt="Avatar"
                        />
                        <span className="text-[13px] font-medium">{capitalizeEachWord(attendee?.userId?.name) || 'Unknown User'}</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-sm">No attendees yet.</p>
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Gallery Modal */}
        {showGallery && (
          <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-white hover:text-gray-300 transition"
            >
              <X className="w-8 h-8" />
            </button>

            <div className="max-w-4xl w-full bg-white rounded-lg overflow-hidden">
              {/* Conditional content based on error state */}
              {galleryError ? (
                <div className="flex flex-col items-center justify-center h-96 p-8">
                  <X className="w-12 h-12 text-red-500 mb-4"/>
                  <h2 className="text-xl font-bold text-red-600 mb-2">Image Not Valid</h2>
                  <p className="text-gray-600 text-center">{galleryError}</p>
                </div>
              ) : (
                // Display the image if no error
                <img
                  src={showGallery}
                  alt="Gallery View"
                  className="w-full h-auto max-h-[80vh] object-contain"
                />
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default EventDetailPage;